import { ChangeEvent, FunctionComponent, useCallback, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useTranslation } from "react-i18next";

import {
	faBuilding,
	faGlobe,
	faLink,
	faUpload,
	faUserPlus,
} from "@fortawesome/pro-regular-svg-icons";

import { useSingleFlight } from "@/utils/useSingleFlight";
import { TRACKER_UPLOAD_FILE_TYPES } from "@/utils/trackerUploadFileTypes";

import { Icon } from "@/components/Icon/Icon";
import { InputField } from "@/components/InputField/InputField";

import { TrackerEntityKind } from "@/enum/tracker.enum";

import { useCreateTrackerFileMutation } from "@/pages/Private/redux/profileTracker/profileTracker.api";

import { TrackerColumnMapping, TrackerMappingModal } from "./TrackerMappingModal";
import { entityKindKey, suggestMappingForHeader } from "./trackerSourceRequirements";
import { readTrackerSourceColumns } from "./readTrackerSourceColumns";

interface TrackerUploadSourceProps {
	listId: number;
	entityKind: TrackerEntityKind;
	/** Routes the user to the list builder when they have no suitable file yet. */
	onCreateListInstead: (entityKind: TrackerEntityKind) => void;
}

/**
 * Adding an uploaded file as a tracker source (PREC-3417).
 *
 * The upload is a two-step affair on purpose: picking the file opens the mapping modal, and
 * nothing is sent until the required identifier is mapped there. Before this, a file could be
 * uploaded with no identifier at all and quietly track nobody.
 */
export const TrackerUploadSource: FunctionComponent<TrackerUploadSourceProps> = ({
	listId,
	entityKind,
	onCreateListInstead,
}) => {
	const { t } = useTranslation();
	const kind = entityKindKey(entityKind);

	const [createTrackerFile, { isLoading }] = useCreateTrackerFileMutation();
	const { run, isRunning } = useSingleFlight();

	const [name, setName] = useState("");
	const [file, setFile] = useState<File | null>(null);
	const [mapping, setMapping] = useState<TrackerColumnMapping[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	const handleFile = useCallback(
		async (picked: File | File[]) => {
			const singleFile = Array.isArray(picked) ? picked[0] : picked;

			setError(null);
			setFile(singleFile);

			if (!name) {
				setName(singleFile.name);
			}

			const columns = await readTrackerSourceColumns(singleFile);

			setMapping(
				columns.map((column) => ({
					...column,
					assignedField: suggestMappingForHeader(column.foundField, entityKind),
				}))
			);
		},
		[entityKind, name]
	);

	const reset = () => {
		setFile(null);
		setMapping(null);
		setName("");
	};

	const handleConfirm = () =>
		run(async () => {
			if (!file || !mapping) {
				return;
			}

			const formData = new FormData();

			formData.append("file", file);
			formData.append("name", name || file.name);
			formData.append("listId", listId.toString());
			formData.append("type", "csv");
			formData.append("entityKind", entityKind);
			formData.append(
				"configuration",
				JSON.stringify(
					mapping.map(({ foundField, assignedField }) => ({ foundField, assignedField }))
				)
			);

			try {
				await createTrackerFile(formData).unwrap();
				reset();
			} catch {
				// The API rejects a mapping that cannot identify the entity; surface that rather
				// than closing the modal on a source that was never stored.
				setError(t("profileTracker.sources.errors.uploadFailed"));
			}
		});

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center gap-2 rounded-lg bg-status-warning-light px-3 py-2 text-xs font-semibold text-brand-black">
				<Icon
					className="h-3.5 w-3.5 flex-shrink-0"
					icon={entityKind === TrackerEntityKind.ORGANIZATION ? faGlobe : faLink}
				/>
				<span>{t(`profileTracker.sources.required.${kind}`)}</span>
			</div>

			<div className="flex flex-wrap items-center gap-2.5">
				<span className="text-xs text-text-meta">{t(`profileTracker.sources.escape.${kind}`)}</span>
				<button
					className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-brand-light duration-200 hover:border-brand-light"
					type="button"
					onClick={() => onCreateListInstead(entityKind)}
				>
					<Icon
						className="h-3.5 w-3.5"
						icon={entityKind === TrackerEntityKind.ORGANIZATION ? faBuilding : faUserPlus}
					/>
					{t(
						`profileTracker.sources.escape.${
							entityKind === TrackerEntityKind.ORGANIZATION ? "createCompanies" : "createPeople"
						}`
					)}
				</button>
			</div>

			<InputField
				handleChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
				label={t("profileTracker.sources.name.label")}
				name="trackerSourceName"
				placeholder={t(
					`profileTracker.sources.name.${
						entityKind === TrackerEntityKind.ORGANIZATION
							? "companiesPlaceholder"
							: "peoplePlaceholder"
					}`
				)}
				value={name}
			/>

			<FileUploader handleChange={handleFile} name="file" types={TRACKER_UPLOAD_FILE_TYPES}>
				<div className="flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-gray-200 bg-white p-10 text-center duration-200 hover:border-brand-light">
					<span className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-blue-action-200 text-brand-light">
						<Icon className="h-4 w-4" icon={faUpload} />
					</span>
					<span className="text-sm">
						{t("profileTracker.sources.dropzone.prompt")}{" "}
						<span className="font-semibold text-brand-light underline">
							{t("profileTracker.sources.dropzone.choose")}
						</span>
					</span>
					<span className="text-xs text-text-meta">
						{t(
							`profileTracker.sources.dropzone.${
								entityKind === TrackerEntityKind.ORGANIZATION ? "companiesHint" : "peopleHint"
							}`
						)}
					</span>
				</div>
			</FileUploader>

			{error && <div className="text-ssm text-status-error">{error}</div>}

			{mapping && file && (
				<TrackerMappingModal
					entityKind={entityKind}
					fileName={file.name}
					isSaving={isLoading || isRunning}
					isSuggesting={false}
					mapping={mapping}
					onChange={setMapping}
					onClose={reset}
					onConfirm={handleConfirm}
				/>
			)}
		</div>
	);
};
